import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Globe, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const PHONE = '+33674626368';
const PHONE_DISPLAY = '+33 6 74 62 63 68';
const LANGS = [
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const navLinks = [
    { label: t('nav.services'), href: '/#services' },
    { label: t('nav.fleet'), href: '/#flotte' },
    { label: t('nav.pricing'), href: '/tarifs' },
    { label: t('nav.contact'), href: '/#contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const handleNavClick = (href) => {
    setMenuOpen(false);
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
  };

  const currentLang = (i18n.language || 'fr').slice(0, 2);
  const setLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18n_language', code);
    setLangOpen(false);
  };

  const LangDropdown = ({ testId }) => (
    <div className="absolute right-0 top-full mt-2 bg-[#111] border border-white/10 rounded-xl overflow-hidden shadow-2xl shadow-black/60 z-[60] min-w-[130px]" data-testid={testId}>
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`${testId}-option-${l.code}`}
          className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${
            currentLang === l.code ? 'text-[#EAB308] bg-white/5 font-semibold' : 'text-[#A3A3A3] hover:text-white hover:bg-white/5'
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-black/80 border-b border-white/10 shadow-lg shadow-black/20'
          : 'backdrop-blur-md bg-black/60 border-b border-white/5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-1 shrink-0">
            <span className="font-bold text-lg sm:text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#EAB308]">Yellow</span>
              <span className="text-white"> Cab</span>
              <span className="text-[#A3A3A3] font-light"> Corsica</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav data-testid="desktop-nav" className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-[#A3A3A3] hover:text-white transition-colors duration-200 text-sm font-medium tracking-wide"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                data-testid="lang-switcher"
                className="flex items-center gap-1.5 text-[#A3A3A3] hover:text-white transition-colors duration-200 text-sm font-medium border border-white/15 hover:border-white/30 rounded-full px-3 py-1.5"
                aria-label="Changer de langue"
              >
                <Globe size={13} />
                {currentLang.toUpperCase()}
                <ChevronDown size={11} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && <LangDropdown testId="lang-dropdown" />}
            </div>
            <a
              href={`tel:${PHONE}`}
              data-testid="header-phone"
              className="flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors duration-200"
            >
              <Phone size={15} />
              <span className="text-sm font-medium">{PHONE_DISPLAY}</span>
            </a>
            <button
              onClick={() => handleNavClick('/#reservation')}
              data-testid="header-reserve-btn"
              className="bg-[#EAB308] hover:bg-[#CA8A04] text-black font-semibold px-5 py-2.5 rounded-full text-sm transition-colors duration-300"
            >
              {t('nav.book')}
            </button>
          </div>

          {/* Mobile right side: lang + menu */}
          <div className="md:hidden flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                data-testid="lang-switcher-mobile"
                className="flex items-center gap-1 text-[#A3A3A3] hover:text-white text-xs font-semibold border border-white/15 rounded-full px-2.5 py-1.5"
                aria-label="Changer de langue"
              >
                <Globe size={11} />
                {currentLang.toUpperCase()}
                <ChevronDown size={10} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && <LangDropdown testId="lang-dropdown-mobile" />}
            </div>
            <button
              className="text-white p-2 -mr-2"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Ouvrir le menu"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-[#0D0D0D] border-b border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="block w-full text-left text-[#A3A3A3] hover:text-white py-3 text-base font-medium transition-colors border-b border-white/5 last:border-0"
                >
                  {link.label}
                </button>
              ))}
              <a
                href={`tel:${PHONE}`}
                data-testid="mobile-phone-link"
                className="flex items-center gap-2 text-[#EAB308] font-semibold py-3 mt-2"
              >
                <Phone size={16} />
                {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
